import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BatterySettingsRequest,
  ChargeMode,
  Checklist,
  EnergyHistory,
  HistoryPeriod,
  PowerHistory,
  SiteInfo,
} from '@ste/shared';
import { apiFetch } from './auth.ts';

export interface SessionSummary {
  id: number;
  created: string;
  finished: string;
  loadpoint: string;
  vehicle: string;
  chargedEnergy: number;
  chargeDuration: number | null;
  solarPercentage?: number;
  price?: number;
}

export interface GridSession {
  id: number;
  created?: string;
  finished?: string;
  type: string;
  limit: number;
}

export const useSite = (enabled = true) =>
  useQuery({ queryKey: ['site'], queryFn: () => apiFetch<SiteInfo>('/site'), staleTime: 60_000, enabled });

/** signed commissioning protocol; 404 (no data) until the installer releases it */
export const useProtocol = (enabled = true) =>
  useQuery({
    queryKey: ['protocol'],
    queryFn: () => apiFetch<Checklist>('/protocol'),
    retry: false,
    staleTime: 300_000,
    enabled,
  });

export const useEnergyHistory = (period: HistoryPeriod, date: string) =>
  useQuery({
    queryKey: ['history', 'energy', period, date],
    queryFn: () => apiFetch<EnergyHistory>(`/history/energy?period=${period}&date=${date}`),
    staleTime: 60_000,
  });

export const usePowerHistory = (from: Date, to: Date, signals: string[], enabled = true) =>
  useQuery({
    queryKey: ['history', 'power', from.toISOString(), to.toISOString(), signals.join(',')],
    queryFn: () =>
      apiFetch<PowerHistory>(
        `/history/power?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}&signals=${signals.join(',')}`,
      ),
    enabled,
    staleTime: 60_000,
  });

export const useSessions = () =>
  useQuery({
    queryKey: ['sessions'],
    queryFn: () => apiFetch<SessionSummary[]>('/sessions'),
    staleTime: 60_000,
  });

export const useGridSessions = () =>
  useQuery({
    queryKey: ['grid-sessions'],
    queryFn: () => apiFetch<GridSession[]>('/grid-sessions'),
    staleTime: 60_000,
  });

const post = (path: string, body?: unknown, method = 'POST') =>
  apiFetch<void>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });

/** commands: live data reflects the result via the websocket, so nothing to invalidate except sessions */
export function useCommands() {
  const client = useQueryClient();
  const options = { onSettled: () => void client.invalidateQueries({ queryKey: ['sessions'] }) };
  return {
    setMode: useMutation({
      mutationFn: ({ id, mode }: { id: number; mode: ChargeMode }) =>
        post(`/loadpoints/${id}/mode`, { mode }),
      ...options,
    }),
    setLimitSoc: useMutation({
      mutationFn: ({ id, soc }: { id: number; soc: number }) => post(`/loadpoints/${id}/limit-soc`, { soc }),
    }),
    setMinTemperature: useMutation({
      mutationFn: ({ id, temperature }: { id: number; temperature: number }) =>
        post(`/loadpoints/${id}/min-temperature`, { temperature }),
    }),
    selectVehicle: useMutation({
      mutationFn: ({ id, vehicle }: { id: number; vehicle: string }) =>
        post(`/loadpoints/${id}/vehicle`, { vehicle }),
    }),
    setPlan: useMutation({
      mutationFn: ({ id, time, soc, energy }: { id: number; time: string; soc?: number; energy?: number }) =>
        post(`/loadpoints/${id}/plan`, { time, soc, energy }),
    }),
    deletePlan: useMutation({
      mutationFn: ({ id }: { id: number }) => post(`/loadpoints/${id}/plan`, undefined, 'DELETE'),
    }),
    setBattery: useMutation({
      mutationFn: (settings: BatterySettingsRequest) => post('/battery', settings, 'PUT'),
    }),
  };
}
